/**
 * Shared scroll ticker for the 3D band deck.
 *
 * Every band would otherwise install its own scroll listener; a long page has
 * fourteen of them. One listener and one rAF pass drives every registered
 * element instead, so the cost stays flat as pages grow.
 */

type Registered = { el: HTMLElement; strength: number }

const nodes = new Set<Registered>()
let frame = 0
let listening = false

/** Maps a band's position in the viewport to its place in the rotating deck. */
function apply(entry: Registered) {
  const { el, strength } = entry
  const rect = el.getBoundingClientRect()
  const viewport = window.innerHeight

  // -1 when the band is a screen above centre, +1 when a screen below.
  const centre = rect.top + rect.height / 2
  const offset = (centre - viewport / 2) / viewport
  const clamped = Math.max(-1.4, Math.min(1.4, offset))

  // The band you are reading sits at the front of the deck; the ones above and
  // below recede and dim, so a long scroll reads as cards advancing past you.
  //
  // Only scale, translate and opacity are used. Both 3D options were tried and
  // rejected after testing clicks against each transform in isolation:
  //
  //  - `translateZ` in the `preserve-3d` stack lets neighbouring bands sit in
  //    front of the receding one, and they swallow clicks aimed at its
  //    buttons.
  //  - `rotateX` is safe on a small card but not on a band: these are hundreds
  //    to thousands of pixels tall, so rotating about the centre throws the
  //    far edge a long way in z. Perspective then shrinks the controls down
  //    there and they stop hit-testing where they are painted.
  //
  // Scale is concentric and uniform, so every control stays exactly where it
  // appears. The 3D tilt still runs on the individual cards, which are small
  // enough for it to be safe.
  const scale = 1 - Math.abs(clamped) * 0.05 * strength
  const translateY = clamped * -14 * strength
  const opacity = 1 - Math.abs(clamped) * 0.18 * strength

  el.style.transform = `translate3d(0, ${translateY.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`
  el.style.opacity = opacity.toFixed(3)
}

function tick() {
  frame = 0
  for (const entry of nodes) apply(entry)
}

function schedule() {
  if (frame) return
  frame = requestAnimationFrame(tick)
}

function ensureListening() {
  if (listening) return
  listening = true
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('resize', schedule, { passive: true })
}

function stopListening() {
  if (!listening || nodes.size > 0) return
  listening = false
  window.removeEventListener('scroll', schedule)
  window.removeEventListener('resize', schedule)
  if (frame) cancelAnimationFrame(frame)
  frame = 0
}

export function registerDeckCard(el: HTMLElement, strength = 1): () => void {
  const entry: Registered = { el, strength }
  nodes.add(entry)
  ensureListening()
  apply(entry)

  return () => {
    nodes.delete(entry)
    el.style.transform = ''
    el.style.opacity = ''
    stopListening()
  }
}
